import { renderToString } from 'react-dom/server';
import Highcharts from 'highcharts';
import { ChartTooltip } from '../components/ChartTooltip';
import {
  ChartDataPoint,
  GroupByOption,
  formatDateForGroupBy,
  formatCurrencyShort,
  reduceByCurrency,
  reduceByCompany,
} from '../../../services/marketPerformanceService';

interface ChartOptionsParams {
  data: ChartDataPoint[];
  selectedGroupBy: GroupByOption;
  currency: string;
  marketUuid?: string;
}

export const buildChartOptions = ({
  data,
  selectedGroupBy,
  currency,
  marketUuid,
}: ChartOptionsParams): Highcharts.Options => {
  const getExportFilename = (): string => {
    const dateStr = new Date().toISOString().split('T')[0].replace(/-/g, '');
    return marketUuid ? `${marketUuid}_OverallMarketPerformance_${dateStr}` : `OverallMarketPerformance_${dateStr}`;
  };

  /* eslint-disable @typescript-eslint/no-explicit-any */
  return {
    chart: {
      type: 'column',
      height: 480,
      backgroundColor: 'transparent',
      zoomType: 'x',
      style: {
        fontFamily: 'system-ui, -apple-system, sans-serif',
      },
      spacing: [15, 15, 20, 15],
    },
    title: {
      text: '',
    },
    accessibility: {
      enabled: true,
      description:
        'Market performance chart showing yield percentage, revenue in millions, and divisions awarded over time',
    },
    exporting: {
      enabled: true,
      filename: getExportFilename(),
      csv: {
        dateFormat: '%Y-%m-%d',
      },
      buttons: {
        contextButton: {
          menuItems: ['downloadCSV'],
        },
      },
    },
    plotOptions: {
      column: {
        minPointLength: 3,
        pointPadding: 0.15,
        groupPadding: 0.1,
        borderWidth: 0,
        borderRadius: 6,
      },
      areaspline: {
        fillOpacity: 0.15,
        lineWidth: 2.5,
        marker: {
          enabled: true,
          lineWidth: 2,
          radius: 5,
          symbol: 'circle',
        },
      },
      spline: {
        lineWidth: 2,
        marker: {
          enabled: true,
          lineWidth: 2,
          radius: 5,
        },
        dashStyle: 'ShortDash',
      },
    },
    xAxis: {
      type: 'datetime',
      tickPixelInterval: 100,
      minPadding: 0.02,
      maxPadding: 0.02,
      gridLineWidth: 0,
      lineColor: '#e5e7eb',
      tickColor: '#e5e7eb',
      labels: {
        enabled: true,
        formatter: function (this: any) {
          return formatDateForGroupBy(new Date(this.value as number), selectedGroupBy.value, true);
        },
        style: {
          fontSize: '11px',
          color: '#6b7280',
          fontWeight: '500',
        },
      },
      crosshair: {
        width: 1,
        color: '#d1d5db',
        dashStyle: 'Dash',
      },
    },
    yAxis: [
      {
        title: {
          text: 'Yield',
          style: {
            fontSize: '12px',
            fontWeight: '600',
            color: '#374151',
          },
        },
        labels: {
          format: '{value}%',
          style: {
            fontSize: '11px',
            color: '#6b7280',
          },
        },
        opposite: true,
        min: 0,
        gridLineColor: '#f3f4f6',
        gridLineDashStyle: 'Dot',
      },
      {
        title: {
          text: 'Revenue',
          style: {
            fontSize: '12px',
            fontWeight: '600',
            color: '#374151',
          },
        },
        labels: {
          formatter: function (this: any) {
            return formatCurrencyShort(this.value as number);
          },
          style: {
            fontSize: '11px',
            color: '#6b7280',
          },
        },
        gridLineWidth: 0,
      },
      {
        title: {
          text: 'Divisions',
          style: {
            fontSize: '12px',
            fontWeight: '600',
            color: '#374151',
          },
        },
        opposite: true,
        min: 0,
        gridLineWidth: 0,
        labels: {
          style: {
            fontSize: '11px',
            color: '#6b7280',
          },
        },
      },
    ],
    tooltip: {
      shared: true,
      crosshairs: [true],
      useHTML: true,
      outside: true,
      backgroundColor: 'transparent',
      borderWidth: 0,
      shadow: false,
      padding: 0,
      positioner: function (this: any, labelWidth: number, labelHeight: number, point: any): { x: number; y: number } {
        const chartBox = this.chart.container.getBoundingClientRect();
        let tooltipX: number = point.plotX + this.chart.plotLeft + 24;

        if (point.plotX > labelWidth) {
          tooltipX = point.plotX + this.chart.plotLeft - labelWidth - 24;
        }

        tooltipX = Math.max(this.chart.plotLeft + 10, tooltipX);

        const tooltipY = Math.max(10, Math.min(chartBox.height - labelHeight - 10, 50));

        return { x: tooltipX, y: tooltipY };
      },
      formatter: function (this: any) {
        const point = this.points?.[0]?.point;
        if (!point) return '';

        const date = new Date(point.x as number);
        const dateStr = formatDateForGroupBy(date, selectedGroupBy.value, false);

        const revenuePoint = this.points?.find((p: any) => p.series.name === 'Revenue');
        const yieldPoint = this.points?.find((p: any) => p.series.name === 'Yield');
        const divisionsPoint = this.points?.find((p: any) => p.series.name === 'Divisions Awarded');

        const dataPoint = data.find((d) => d.date.getTime() === (point.x as number));
        const arAwarded = dataPoint?.clearedInvoiceAmount ?? 0;

        const currencyBreakdown =
          !marketUuid && dataPoint?.records ? reduceByCurrency(dataPoint.records, currency) : undefined;
        const companyBreakdown =
          !marketUuid && dataPoint?.records ? reduceByCompany(dataPoint.records, currency) : undefined;

        return renderToString(
          <ChartTooltip
            dateStr={dateStr}
            gmr={revenuePoint?.y ?? 0}
            yieldValue={yieldPoint?.y ?? 0}
            arAwarded={arAwarded}
            divisionsAwarded={divisionsPoint?.y ?? 0}
            currency={currency}
            currencyBreakdown={currencyBreakdown}
            companyBreakdown={companyBreakdown}
          />,
        );
      },
    },
    legend: {
      align: 'center',
      verticalAlign: 'bottom',
      layout: 'horizontal',
      itemStyle: {
        fontSize: '12px',
        fontWeight: '500',
        color: '#374151',
      },
      itemHoverStyle: {
        color: '#111827',
      },
      symbolRadius: 4,
      symbolHeight: 10,
      symbolWidth: 10,
      itemDistance: 24,
      margin: 20,
    },
    series: [
      {
        name: 'Yield',
        type: 'areaspline',
        color: '#9BD045',
        fillColor: {
          linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
          stops: [
            [0, 'rgba(155, 208, 69, 0.25)'],
            [1, 'rgba(155, 208, 69, 0.02)'],
          ],
        },
        data: data.map((d) => [d.date.getTime(), d.yield]),
        yAxis: 0,
        zIndex: 3,
        marker: {
          symbol: 'circle',
          fillColor: '#fff',
          lineColor: '#9BD045',
          lineWidth: 2,
          radius: 5,
          states: {
            hover: {
              fillColor: '#9BD045',
              lineColor: '#9BD045',
              radius: 7,
            },
          },
        },
      },
      {
        name: 'Revenue',
        type: 'column',
        color: '#06A76C',
        data: data.map((d) => [d.date.getTime(), d.revenue]),
        yAxis: 1,
        states: {
          hover: {
            brightness: 0.1,
          },
        },
      },
      {
        name: 'Divisions Awarded',
        type: 'spline',
        color: '#369DD9',
        data: data.map((d) => [d.date.getTime(), d.divisionsAwarded]),
        yAxis: 2,
        zIndex: 4,
        marker: {
          symbol: 'circle',
          fillColor: '#fff',
          lineColor: '#369DD9',
          lineWidth: 2,
          states: {
            hover: {
              fillColor: '#369DD9',
              lineColor: '#369DD9',
              radius: 7,
            },
          },
        },
      },
    ],
    credits: {
      enabled: false,
    },
  } as Highcharts.Options;
  /* eslint-enable @typescript-eslint/no-explicit-any */
};
